import { SECOND_MS } from "~/utils/constants";

/**
 * Mirrors the API's terminal hold (150 s) plus the slack its own settlement
 * wait allows, so the till gives up no earlier than the server does.
 */
export const CARD_ORDER_RECOVERY_DEADLINE_MS = 180 * SECOND_MS;
export const CARD_ORDER_RECOVERY_INTERVAL_MS = 2 * SECOND_MS;
/**
 * A request that reached the API creates the order within a second or two; a
 * 404 before this grace period may simply be racing the request itself.
 */
export const CARD_ORDER_NOT_FOUND_GRACE_MS = 10 * SECOND_MS;

type RecoverableOrder = {
  status: string;
  payments?: { id: string }[] | null;
};

type FetchOrderResult =
  | { status: 200; body: RecoverableOrder }
  | { status: number; body?: unknown };

export type RecoverCardOrderOutcome =
  | { outcome: "paid"; paymentId: string }
  | { outcome: "cancelled" }
  | { outcome: "not_found" }
  | { outcome: "timed_out" };

type RecoverCardOrderOptions = {
  startedAt: number;
  fetchOrder: () => Promise<FetchOrderResult>;
  /**
   * Whether the terminal has a transaction for this attempt's service id. A
   * missing order only proves nothing was charged when the terminal never saw
   * the request either.
   */
  isTerminalTransactionKnown: () => Promise<boolean>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  deadlineMs?: number;
  intervalMs?: number;
  notFoundGraceMs?: number;
};

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Finds out what happened to a card order whose create request lost its
 * response. The order id is minted on the till, so the order can be polled
 * directly until the server has settled it one way or the other.
 */
export async function recoverCardOrder({
  startedAt,
  fetchOrder,
  isTerminalTransactionKnown,
  now = Date.now,
  sleep = defaultSleep,
  deadlineMs = CARD_ORDER_RECOVERY_DEADLINE_MS,
  intervalMs = CARD_ORDER_RECOVERY_INTERVAL_MS,
  notFoundGraceMs = CARD_ORDER_NOT_FOUND_GRACE_MS
}: RecoverCardOrderOptions): Promise<RecoverCardOrderOutcome> {
  while (now() - startedAt < deadlineMs) {
    const res = await fetchOrder().catch(() => null);

    if (res?.status === 200) {
      const order = res.body as RecoverableOrder;

      if (order.status === "paid") {
        return {
          outcome: "paid",
          paymentId: order.payments?.[0]?.id ?? "unknown"
        };
      }

      if (order.status === "cancelled" || order.status === "failed") {
        return { outcome: "cancelled" };
      }
    } else if (res?.status === 404 && now() - startedAt >= notFoundGraceMs) {
      const known = await isTerminalTransactionKnown().catch(() => true);

      if (!known) {
        return { outcome: "not_found" };
      }
    }

    await sleep(intervalMs);
  }

  return { outcome: "timed_out" };
}
